import type { DeepPartial } from '@gitbook/util-ts';

import { ensureSpaceScreenMode, normalizeNavigationScreen } from './normalize';
import {
    ALL_SIDESHEET_KEYS,
    type AllSideSheets,
    getNavigationSideSheetTabs,
    getSpaceSideSheetKeys,
} from './side-sheets';
import {
    isNavigationScreenSpaceChangeRequestState,
    type NavigationScreenCollectionState,
    type NavigationScreenSiteState,
    type NavigationScreenSpaceState,
    type NavigationScreenState,
} from './types';

export type NavigationScreenModifier =
    | DeepPartial<NavigationScreenState>
    | ((prev: NavigationScreenState) => DeepPartial<NavigationScreenState>);

/**
 * Apply a transform on a screen and return a normalized version.
 * This function can throw an error if the result is not valid.
 */
export function transformNavigationScreen(
    screen: NavigationScreenState,
    transform: NavigationScreenModifier
): NavigationScreenState {
    const newScreen = mergeNavigationScreen(
        {
            ...screen,
            // Reset the hash when transforming a screen
            ...{ hash: undefined },
        },
        typeof transform === 'function' ? transform(screen) : transform
    );

    // @ts-ignore
    return normalizeNavigationScreen(newScreen);
}

/**
 * Merge a partial screen into a complete screen.
 * Normalizing it at the same time.
 */
function mergeNavigationScreen(
    screen: NavigationScreenState,
    partial: DeepPartial<NavigationScreenState>
) {
    // The welcome screen is never organization-scoped: switching to it must not
    // carry over the previous screen's `organization` through the generic spread below.
    if (partial.type === 'welcome') {
        return { ...partial };
    }

    switch (screen.type) {
        case 'site':
            partial = normalizeSideSheet(partial as DeepPartial<NavigationScreenSiteState>);

            /**
             * When switching to a different type, reset all site attributes.
             */
            if (
                (partial.type && partial.type !== 'site') ||
                (partial.site && screen.site !== partial.site)
            ) {
                return {
                    type: screen.type,
                    // Most links are to spaces in the same org, so we are optimistic and keep the org the same.
                    // The `SpaceScreen` should handle the case where the space is in a different org.
                    organization: screen.organization,
                    ...partial,
                };
            }

            return {
                ...screen,
                ...partial,
            };
        case 'space':
            partial = normalizeSideSheet(partial as DeepPartial<NavigationScreenSpaceState>);

            if (partial.type === 'space' && partial.space && screen.space !== partial.space) {
                // When switching to a new space, reset everything
                return {
                    // Most links are to spaces in the same org, so we are optimistic and keep the org the same.
                    // The `SpaceScreen` should handle the case where the space is in a different org.
                    organization: screen.organization,
                    site: screen.site,
                    ...partial,
                };
            }

            // Normalize switching to another screen of the same space
            if (screen.type === 'space' && (partial.type === 'space' || !partial.type)) {
                return normalizeSpaceSwitcher(
                    screen,
                    partial as DeepPartial<NavigationScreenSpaceState>
                );
            }

            return {
                ...screen,
                ...partial,
            };
        case 'collection':
            partial = normalizeSideSheet(partial as DeepPartial<NavigationScreenCollectionState>);

            if (
                partial.type === 'collection' &&
                partial.collection &&
                screen.collection !== partial.collection
            ) {
                return {
                    // Most links are to spaces in the same org, so we are optimistic and keep the org the same.
                    // The `SpaceScreen` should handle the case where the space is in a different org.
                    organization: screen.organization,
                    ...partial,
                };
            }
            return {
                ...screen,
                ...partial,
            };
        case 'org_settings':
            if (partial.type && partial.type !== 'org_settings') {
                return {
                    type: screen.type,
                    organization: screen.organization,
                    ...partial,
                };
            }

            if (
                !partial.type &&
                'tab' in screen &&
                screen.tab.type === 'integrations' &&
                !('tab' in partial)
            ) {
                const { category, search, provider, ...rest } = partial as {
                    category?: unknown;
                    search?: string;
                    provider?: unknown;
                };
                return {
                    ...screen,
                    ...rest,
                    tab: {
                        ...screen.tab,
                        ...('category' in partial ? { category: category as never } : {}),
                        ...('search' in partial ? { search } : {}),
                        ...('provider' in partial ? { provider: provider as never } : {}),
                    },
                };
            }

            if (
                !partial.type &&
                'tab' in screen &&
                screen.tab.type === 'openapi' &&
                !('tab' in partial) &&
                'spec' in partial
            ) {
                const { spec, ...rest } = partial as { spec?: string };
                return {
                    ...screen,
                    ...rest,
                    tab: {
                        ...screen.tab,
                        spec,
                    },
                };
            }

            if (
                !partial.type &&
                'tab' in screen &&
                screen.tab.type === 'translations' &&
                !('tab' in partial) &&
                'view' in partial
            ) {
                const { view, ...rest } = partial as { view?: 'overview' | 'glossary' };
                return {
                    ...screen,
                    ...rest,
                    tab: {
                        ...screen.tab,
                        view,
                    },
                };
            }

            return {
                ...screen,
                ...partial,
            };
        default:
            return {
                ...screen,
                ...partial,
            };
    }
}

/**
 * Normalize switching between screens of the same space.
 */
function normalizeSpaceSwitcher(
    from: NavigationScreenSpaceState,
    to: DeepPartial<NavigationScreenSpaceState>
): NavigationScreenState {
    const modifier = { ...to };

    // Close the CR panels when we go back to primary content
    if (
        isNavigationScreenSpaceChangeRequestState(from) &&
        'revision' in to &&
        to.revision === undefined &&
        'changeRequest' in to &&
        to.changeRequest === undefined
    ) {
        modifier.sideSheet = undefined;
    }

    const merged = Object.assign({}, from, modifier);

    // Ensure default mode is set
    ensureSpaceScreenMode(merged);

    // The selected change belongs to the change request overview; deleted rather than set to
    // undefined, as the revision screen's strict schema rejects the key altogether.
    if ('change' in merged && (!merged.changeRequest || merged.mode !== 'overview')) {
        delete merged.change;
    }

    // PageOptions side sheet is only meaningful in editor mode
    if (merged.sideSheet?.type === 'page' && merged.mode !== 'edit') {
        return {
            ...merged,
            sideSheet: undefined,
        };
    }

    // Validate that the sideSheet is valid for the merged state
    if (merged.sideSheet) {
        const validSideSheetKeys = getSpaceSideSheetKeys(merged);
        if (!validSideSheetKeys.includes(merged.sideSheet.type)) {
            return {
                ...merged,
                sideSheet: undefined,
            };
        }
    }

    return merged;
}

/**
 * Normalize a sidesheet.
 */
function normalizeSideSheet<
    State extends
        | NavigationScreenSiteState
        | NavigationScreenSpaceState
        | NavigationScreenCollectionState,
>(to: DeepPartial<State>): DeepPartial<State> {
    // No sidesheet
    if (!to.sideSheet) {
        return to;
    }

    // If the "type" is missing from the sidesheet, we remove it entirely
    if (
        !('type' in to.sideSheet) ||
        !to.sideSheet.type ||
        !ALL_SIDESHEET_KEYS.includes(to.sideSheet.type as AllSideSheets['type'])
    ) {
        return {
            ...to,
            sideSheet: undefined,
        };
    }

    const sideSheet = to.sideSheet as AllSideSheets;
    const tabs = getNavigationSideSheetTabs(sideSheet.type);

    if (!('tab' in sideSheet) || !sideSheet.tab || !tabs.length) {
        return to; // No tab defined, but this sidesheet doesn't have tabs, return as-is
    }

    if (!tabs.includes(sideSheet.tab)) {
        return {
            ...to,
            sideSheet: {
                ...sideSheet,
                tab: tabs[0], // Tab is defined but it's not a valid tab, reset to the first tab
            },
        };
    }

    return to;
}
